import { toast } from "@/components/ToastNotification";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { AbaConfiguracoes } from "./components/AbaConfiguracoes";
import { AbaContatos } from "./components/AbaContatos";
import { AbaDocumentos } from "./components/AbaDocumentos";
import { AbaEnderecos } from "./components/AbaEnderecos";
import { AbaInformacoesAdicionais } from "./components/AbaInformacoesAdicionais";
import { AbaPessoa } from "./components/AbaPessoa";
import { PacienteEditProps } from "./interface/Paciente.interface";
import { atualizarPacienteSchema, CriarPacienteFormData, criarPacienteSchema } from "./schemas/paciente.schema";

const PacienteEdit = ({ open, onOpenChange, paciente, onSuccess, onUsuarioCriado }: PacienteEditProps) => {
  const [loading, setLoading] = useState(false);

  // Inicializa o formulário com react-hook-form
  const form = useForm<CriarPacienteFormData>({
    resolver: zodResolver(paciente ? atualizarPacienteSchema : criarPacienteSchema),
    defaultValues: {
      pessoa: {
        nome: "",
        tipo: "FISICA",
        categoria: "PACIENTE",
        data_nascimento: "",
        genero: undefined,
        imagem_perfil: "",
      },
      pessoa_fisica: {
        cpf: "",
        rg: "",
      },
      enderecos: [],
      emails: [],
      telefones: [],
      cartao_sus: "",
      observacoes: "",
      acompanhante: "",
      fone_acompanhante: "",
      endereco_acompanhante: "",
      matricula_convenio: "",
      status: true,
      documentos: [],
      convenios: [],
    },
    mode: "onChange",
  });

  const formData = form.watch();

  useEffect(() => {
    if (paciente) {
      form.reset({
        pessoa: {
          nome: paciente.pessoa?.nome || "",
          tipo: "FISICA",
          categoria: "PACIENTE",
          data_nascimento: paciente.pessoa?.data_nascimento || "",
          genero: (paciente.pessoa?.genero as "MASCULINO" | "FEMININO" | "OUTRO") || undefined,
          imagem_perfil: (paciente.pessoa as any)?.imagem_perfil || "",
        },
        pessoa_fisica: {
          cpf: paciente.pessoa?.pessoaFisica?.cpf || "",
          rg: paciente.pessoa?.pessoaFisica?.rg || "",
        },
        enderecos: paciente.pessoa?.enderecos?.map(e => ({
          cep: e.cep || "",
          logradouro: e.logradouro || "",
          numero: e.numero || "",
          bairro: e.bairro || "",
          cidade: e.cidade || "",
          uf: e.uf || "",
          complemento: e.complemento || "",
          principal: e.principal || false,
        })) || [],
        emails: paciente.pessoa?.emails?.map(e => ({
          email: e.email || "",
          principal: e.principal || false,
        })) || [],
        telefones: paciente.pessoa?.telefones?.map(t => ({
          telefone: t.telefone || "",
          tipo: (t.tipo || "CELULAR") as "CELULAR" | "FIXO" | "WHATSAPP" | "OUTRO",
          principal: t.principal || false,
        })) || [],
        cartao_sus: paciente.cartao_sus || "",
        observacoes: paciente.observacoes || "",
        acompanhante: paciente.acompanhante || "",
        fone_acompanhante: paciente.fone_acompanhante || "",
        endereco_acompanhante: paciente.endereco_acompanhante || "",
        matricula_convenio: paciente.matricula_convenio || "",
        status: paciente.status,
        documentos: (paciente.documentos || []).map(doc => {
          // Extrai o nome do arquivo do caminho se nome_arquivo não estiver disponível
          let nomeArquivo = doc.nome_arquivo;
          if (!nomeArquivo && doc.caminho_arquivo) {
            // Extrai o nome do arquivo do caminho (última parte após a última barra)
            const partesCaminho = doc.caminho_arquivo.split('/');
            nomeArquivo = partesCaminho[partesCaminho.length - 1] || `documento_${doc.tipo_documento}`;
          } else if (!nomeArquivo) {
            // Se não tiver nem nome nem caminho, usa um nome padrão baseado no tipo
            nomeArquivo = `documento_${doc.tipo_documento}`;
          }
          
          return {
            tipo_documento: doc.tipo_documento as "RG" | "CPF" | "CARTAO_SUS" | "COMPROVANTE_ENDERECO" | "OUTRO",
            arquivo: null,
            nome_arquivo: nomeArquivo,
            caminho_arquivo: doc.caminho_arquivo,
            id: doc.id
          };
        }),
        convenios: (paciente.convenios || []).map(conv => ({
          id_convenio: conv.id_convenio,
          matricula: conv.matricula,
          valor_consulta: conv.valor_consulta,
          observacoes: conv.observacoes,
        })),
      });
    } else {
      form.reset({
        pessoa: {
          nome: "",
          tipo: "FISICA",
          categoria: "PACIENTE",
          data_nascimento: "",
          genero: undefined,
          imagem_perfil: "",
        },
        pessoa_fisica: {
          cpf: "",
          rg: "",
        },
        enderecos: [],
        emails: [],
        telefones: [],
        cartao_sus: "",
        observacoes: "",
        acompanhante: "",
        fone_acompanhante: "",
        endereco_acompanhante: "",
        matricula_convenio: "",
        status: true,
        documentos: [],
        convenios: [],
      });
    }
  }, [paciente, open, form]);

  const converterArquivoParaBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (data: CriarPacienteFormData) => {
    setLoading(true);

    try {
      console.log("🚀 Iniciando salvamento do paciente...", data);

      // Converte arquivos para base64
      const documentosComArquivo = await Promise.all(
        data.documentos.map(async (doc) => {
          if (doc.arquivo) {
            const base64 = await converterArquivoParaBase64(doc.arquivo);
            return {
              tipo_documento: doc.tipo_documento,
              nome_arquivo: doc.nome_arquivo,
              arquivo: base64, // Envia como base64
            };
          }
          return {
            tipo_documento: doc.tipo_documento,
            nome_arquivo: doc.nome_arquivo,
          };
        })
      );

      const payload = {
        pessoa: {
          nome: data.pessoa.nome,
          tipo: data.pessoa.tipo,
          categoria: data.pessoa.categoria,
          genero: data.pessoa.genero || undefined,
          data_nascimento: data.pessoa.data_nascimento || undefined,
          imagem_perfil: data.pessoa.imagem_perfil || undefined,
        },
        pessoa_fisica: {
          cpf: data.pessoa_fisica.cpf,
          rg: data.pessoa_fisica.rg || undefined,
        },
        enderecos: data.enderecos?.map((e) => ({
          cep: e.cep || undefined,
          logradouro: e.logradouro,
          numero: e.numero || undefined,
          bairro: e.bairro || undefined,
          cidade: e.cidade || undefined,
          uf: e.uf || undefined,
          complemento: e.complemento || undefined,
          principal: e.principal ?? false,
        })) || [],
        telefones: data.telefones?.map((t) => ({
          telefone: t.telefone,
          tipo: t.tipo || "CELULAR",
          principal: t.principal ?? false,
        })) || [],
        emails: data.emails?.map((e) => ({
          email: e.email,
          principal: e.principal ?? false,
        })) || [],
        cartao_sus: data.cartao_sus || undefined,
        observacoes: data.observacoes || undefined,
        acompanhante: data.acompanhante || undefined,
        fone_acompanhante: data.fone_acompanhante || undefined,
        endereco_acompanhante: data.endereco_acompanhante || undefined,
        matricula_convenio: data.matricula_convenio || undefined,
        status: data.status,
        documentos: documentosComArquivo,
        convenios: data.convenios || [],
      };

      if (paciente) {
        await api.put(`/api/paciente/${paciente.id}`, payload);
        toast.success("Paciente atualizado com sucesso!");
      } else {
        await api.post("/api/paciente", payload);
        toast.success("Paciente criado com sucesso!");
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Erro ao salvar paciente:", error);
      
      // Tratamento melhorado de erros
      let errorMessage = "Erro ao salvar o paciente";
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (errorData.details && Array.isArray(errorData.details)) {
          // Erros de validação do Zod
          const validationErrors = errorData.details.map((issue: any) => {
            const path = issue.path?.join(".") || "campo";
            return `${path}: ${issue.message}`;
          }).join(", ");
          errorMessage = `Erros de validação: ${validationErrors}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{paciente ? "Editar Paciente" : "Novo Paciente"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit, (errors) => {
            // Esta função é chamada quando a validação falha
            console.log("❌ Erros de validação:", errors);
            
            // Coleta todos os erros do formulário
            const camposFaltando: string[] = [];

            // Mapeamento de campos para nomes amigáveis
            const mapeamentoCampos: { [key: string]: string } = {
              'pessoa.nome': 'Nome',
              'pessoa_fisica.cpf': 'CPF',
              'enderecos': 'Endereço',
              'cep': 'CEP',
              'logradouro': 'Logradouro',
              'bairro': 'Bairro',
              'cidade': 'Cidade',
              'uf': 'UF',
            };

            // Função auxiliar para formatar o nome do campo
            const formatarNomeCampo = (path: string): string => {
              // Remove índices de array (ex: enderecos.0.cep -> enderecos.cep)
              const pathLimpo = path.replace(/\.\d+\./g, '.').replace(/\.\d+$/, '');
              
              // Extrai o último campo do caminho
              const partes = pathLimpo.split('.');
              const ultimoCampo = partes[partes.length - 1];
              
              // Se for um campo conhecido, retorna o nome amigável
              if (mapeamentoCampos[ultimoCampo]) {
                return mapeamentoCampos[ultimoCampo];
              }
              
              // Se o caminho completo está no mapeamento
              if (mapeamentoCampos[pathLimpo]) {
                return mapeamentoCampos[pathLimpo];
              }
              
              // Retorna o último campo capitalizado
              return ultimoCampo.charAt(0).toUpperCase() + ultimoCampo.slice(1);
            };

            // Função recursiva para coletar todos os erros
            const coletarErros = (obj: any, prefix = ''): void => {
              if (!obj || typeof obj !== 'object') return;

              Object.keys(obj).forEach((key) => {
                const caminhoCompleto = prefix ? `${prefix}.${key}` : key;
                const valor = obj[key];

                if (valor) {
                  if (typeof valor === 'object') {
                    if (valor.message) {
                      // É um erro com mensagem
                      const nomeCampo = formatarNomeCampo(caminhoCompleto);
                      if (!camposFaltando.includes(nomeCampo)) {
                        camposFaltando.push(nomeCampo);
                      }
                    } else if (Array.isArray(valor)) {
                      // É um array de erros
                      valor.forEach((item, index) => {
                        if (item && typeof item === 'object') {
                          coletarErros(item, `${caminhoCompleto}.${index}`);
                        }
                      });
                    } else {
                      // É um objeto aninhado
                      coletarErros(valor, caminhoCompleto);
                    }
                  }
                }
              });
            };

            coletarErros(errors);

            // Formata a mensagem de erro
            let mensagemErro = "Por favor, preencha os seguintes campos obrigatórios:";
            if (camposFaltando.length > 0) {
              mensagemErro += "\n\n" + camposFaltando.map(campo => `• ${campo}`).join("\n");
            } else {
              mensagemErro = "Por favor, corrija os erros no formulário";
            }

            toast.error(mensagemErro, {
              duration: 8000, // Aumenta a duração para dar tempo de ler todos os campos
            });
          })} className="space-y-4">
            <Tabs defaultValue="pessoa" className="w-full">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="pessoa">Pessoa</TabsTrigger>
                <TabsTrigger value="contatos">Contatos</TabsTrigger>
                <TabsTrigger value="enderecos">Endereços</TabsTrigger>
                <TabsTrigger value="documentos">Documentos</TabsTrigger>
                <TabsTrigger value="informacoes">Informações Gerais</TabsTrigger>
                <TabsTrigger value="configuracoes">Configurações</TabsTrigger>
              </TabsList>

              <TabsContent value="pessoa" className="space-y-4">
                <AbaPessoa form={form} formData={formData} />
              </TabsContent>

              <TabsContent value="contatos" className="space-y-4">
                <AbaContatos 
                  form={form}
                  formData={formData} 
                />
              </TabsContent>

              <TabsContent value="informacoes" className="space-y-4">
                <AbaInformacoesAdicionais form={form} formData={formData} />
              </TabsContent>

              <TabsContent value="enderecos" className="space-y-4">
                <AbaEnderecos 
                  form={form}
                  formData={formData} 
                />
              </TabsContent>

              <TabsContent value="documentos" className="space-y-4">
                <AbaDocumentos 
                  form={form}
                  formData={formData} 
                />
              </TabsContent>

              <TabsContent value="configuracoes" className="space-y-4">
                <AbaConfiguracoes
                  form={form}
                  formData={formData}
                  temUsuario={paciente?.tem_usuario}
                  pacienteId={paciente?.id}
                  onUsuarioCriado={onUsuarioCriado}
                />
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (paciente ? "Atualizando..." : "Salvando...") : (paciente ? "Atualizar" : "Salvar")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PacienteEdit;

